import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  useTSQInvStatus,
  useTSMCreateInvStatus,
  useTSMUpdateInvStatus,
} from "../../hooks/useTSInvStatus";
import { InvStatusCreate, InvStatusUpdate } from "../../types/invStatusTypes";

// Validation schema
const invStatusSchema = yup.object({
  name: yup.string().required("Status Name is required"),
  exclude_from_mrp: yup.bool(),
  allow_allocation: yup.bool(),
  allow_picking: yup.bool(),
});

export const PageInvStatusForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const isViewMode = formMode === "view";
  const isEditMode = Boolean(id) && formMode === "edit";
  const isCreateMode = !id && formMode === "create";

  const {
    data: statusData,
    isLoading,
    error: statusError,
  } = useTSQInvStatus(id || "");

  const createStatusMutation = useTSMCreateInvStatus();
  const updateStatusMutation = useTSMUpdateInvStatus();

  const isSaving =
    createStatusMutation.isPending || updateStatusMutation.isPending;

  // State
  const [error, setError] = useState<string | null>(null);

  interface InvStatusFormData {
    name: string;
    exclude_from_mrp: boolean;
    allow_allocation: boolean;
    allow_picking: boolean;
    is_default_receive_status: boolean;
  }

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<InvStatusFormData>({
    resolver: yupResolver(invStatusSchema) as any,
    defaultValues: {
      name: "",
      exclude_from_mrp: false,
      allow_allocation: true,
      allow_picking: true,
    },
  });

  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/inventory/status/form?mode=view&id=${id}`);
  };

  const switchToEditMode = () => {
    navigate(`/inventory/status/form?mode=edit&id=${id}`);
  };

  const switchToCreateMode = () => {
    navigate("/inventory/status/form?mode=create");
  };

  useEffect(() => {
    if (statusData && (isEditMode || isViewMode)) {
      reset({
        name: statusData.name,
        exclude_from_mrp: statusData.exclude_from_mrp,
        allow_allocation: statusData.allow_allocation,
        allow_picking: statusData.allow_picking,
        is_default_receive_status: statusData.is_default_receive_status,
      });
    }
  }, [statusData, isEditMode, isViewMode, reset]);

  // Form submission
  const onSubmit = async (data: InvStatusFormData) => {
    try {
      setError(null);

      if (isCreateMode) {
        const statusPayload: InvStatusCreate = {
          name: data.name,
          exclude_from_mrp: data.exclude_from_mrp,
          allow_allocation: data.allow_allocation,
          allow_picking: data.allow_picking,
          is_default_receive_status: data.is_default_receive_status,
        };
        const new_status = await createStatusMutation.mutateAsync(
          statusPayload
        );
        navigate(`/inventory/status/form?mode=view&id=${new_status.id}`);
      } else if (isEditMode) {
        if (!id) {
          throw new Error("Status ID is required for updates");
        }
        const statusPayload: InvStatusUpdate = {
          id: id,
          name: data.name,
          exclude_from_mrp: data.exclude_from_mrp,
          allow_allocation: data.allow_allocation,
          allow_picking: data.allow_picking,
        };

        await updateStatusMutation.mutateAsync({
          id: id,
          data: statusPayload,
        });
        navigate(`/inventory/status/form?mode=view&id=${id}`);
      }
    } catch (err: any) {
      console.error("Failed to save status:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save status"
      );
    }
  };

  const handleCancel = () => {
    navigate("/inventory/status");
  };

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          variant="outlined"
        >
          Back to Inventory Statuses
        </Button>
        <Typography variant="h4" component="h1">
          {isCreateMode
            ? "Create Status"
            : isViewMode
            ? `View Status: ${statusData?.name || ""}`
            : `Edit Status: ${statusData?.name || ""}`}
        </Typography>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
      </Stack>

      {/* Error Alert */}
      {(error || statusError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error || statusError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Status Name *"
                  fullWidth
                  disabled={isViewMode}
                  error={!!errors.name}
                  helperText={
                    errors.name?.message || "Status names must be unique."
                  }
                />
              )}
            />
            <Divider />

            {/* Settings */}
            <Typography variant="h6" gutterBottom>
              Status Settings
            </Typography>

            <Box sx={{ display: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="is_default_receive_status"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode || isEditMode}
                        />
                      }
                      label="Is Default Receive STatus?"
                    />
                  )}
                />
                <Controller
                  name="exclude_from_mrp"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Exclude From MRP?"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  If turned on, Lots in this status will not be considered when
                  performing MRP calculations.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="allow_allocation"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Allow Allocation?"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  If turned off, Lots in this status will not be considered when
                  auto-allocation is performed.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="allow_picking"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Allow Picking?"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  If turned off, Lots in this status will not be considered when
                  providing lots to choose when fulfilling orders or transfers.
                </Typography>
              </Box>
            </Box>

            {/* Form Actions */}
            {!isViewMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : isCreateMode
                    ? "Create Status"
                    : "Update Status"}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
